/**
 * Shared Sentry helper functions and filters.
 * This factory pattern allows apps to use different Sentry SDKs
 * (@sentry/react, @sentry/react-native, @sentry/nestjs) while sharing
 * the core helper logic.
 *
 * Uses `any` in several places to bridge incompatible SDK types across
 * @sentry/react, @sentry/react-native, and @sentry/nestjs.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Minimal Sentry SDK interface — satisfied by @sentry/react, @sentry/react-native, @sentry/nestjs */
export interface SentryLike {
  captureException: (err: unknown, ctx?: any) => string;
  captureMessage: (msg: string, level?: any) => string;
  setUser: (user: { id: string; email?: string } | null) => void;
  addBreadcrumb: (crumb: Record<string, unknown>) => void;
  withScope: (callback: (scope: any) => void) => void;
}

export type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';

/**
 * Create Sentry helper functions bound to a specific SDK instance.
 *
 * @param sentry - The Sentry SDK module
 * @param dsnConfigured - Whether a DSN is configured (controls dev logging)
 * @returns Helper functions for capturing exceptions, messages, etc.
 */
export function createSentryHelpers(sentry: SentryLike, dsnConfigured: boolean) {
  return {
    captureException: (
      err: unknown,
      context?: {
        tags?: Record<string, string>;
        extra?: Record<string, unknown>;
        level?: SeverityLevel;
      }
    ) => {
      if (!dsnConfigured) {
        console.error('[Sentry would capture]:', err, context);
        return;
      }
      sentry.withScope((scope: any) => {
        if (context?.tags) {
          Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
        }
        if (context?.extra) {
          Object.entries(context.extra).forEach(([k, v]) => scope.setExtra(k, v));
        }
        if (context?.level) {
          scope.setLevel(context.level);
        }
        sentry.captureException(err);
      });
    },

    captureMessage: (msg: string, level: SeverityLevel = 'info') => {
      if (!dsnConfigured) {
        // eslint-disable-next-line no-console
        console.log(`[Sentry ${level}]:`, msg);
        return;
      }
      sentry.captureMessage(msg, level);
    },

    setUser: (user: { id: string; email?: string } | null) => {
      if (!dsnConfigured) return;
      sentry.setUser(user ? { id: user.id, email: user.email } : null);
    },

    addBreadcrumb: (
      msg: string,
      category: string,
      data?: Record<string, unknown>,
      level: SeverityLevel = 'info'
    ) => {
      if (!dsnConfigured) return;
      sentry.addBreadcrumb({
        message: msg,
        category,
        data,
        level,
        timestamp: Date.now() / 1000,
      });
    },
  };
}

/**
 * Shared beforeSend filter — drops AbortError and user-cancelled operations.
 * Use this in Sentry.init({ beforeSend: createBeforeSendFilter() })
 */
export function createBeforeSendFilter() {
  return (event: any, hint: any): any | null => {
    const error = hint?.originalException;
    if (error instanceof Error) {
      if (error.name === 'AbortError' || error.message.includes('user cancelled')) {
        return null;
      }
    }
    return event;
  };
}

/**
 * Shared beforeBreadcrumb filter — strips auth/token URLs from navigation and XHR breadcrumbs.
 * Use this in Sentry.init({ beforeBreadcrumb: createBeforeBreadcrumbFilter() })
 */
export function createBeforeBreadcrumbFilter() {
  return (breadcrumb: any): any | null => {
    if (breadcrumb.category === 'navigation' || breadcrumb.category === 'xhr') {
      const url = breadcrumb.data?.url || breadcrumb.data?.from;
      if (url && (url.includes('/auth') || url.includes('token'))) {
        return null;
      }
    }
    return breadcrumb;
  };
}
